import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Loader2, Camera, Upload, X, AlertTriangle,
  CheckCircle, Thermometer, Zap, Sun,
} from 'lucide-react';
import { api } from '@/utils/api';

const PRODUCT_TYPES = [
  { value: 'ss_water_tank', label: 'SS Water Tank', icon: Thermometer },
  { value: 'heat_pump', label: 'Heat Pump', icon: Zap },
  { value: 'swh', label: 'Solar Water Heater', icon: Sun },
] as const;

const RECEIVED_FROM = [
  { value: 'sales_team', label: 'Sales Team' },
  { value: 'client', label: 'Client' },
  { value: 'dl', label: 'Dealer' },
] as const;

export function ComplaintIntakePage() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    customerName: '',
    customerPhone: '',
    customerAddress: '',
    invoiceNumber: '',
    invoiceDate: '',
    serialNumber: '',
    productType: '' as '' | 'ss_water_tank' | 'heat_pump' | 'swh',
    description: '',
    systemVerification: '',
    receivedFrom: 'client' as 'sales_team' | 'client' | 'dl',
  });

  const [photos, setPhotos] = useState<string[]>([]);
  const [photoUploading, setPhotoUploading] = useState(false);

  const isComplete = form.customerName.trim() && form.customerPhone.trim() && 
                     form.productType && form.description.trim();

  const handleSubmit = async () => {
    if (!isComplete) {
      setError('Please fill all required fields');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await api.post('/service-sop/complaints', {
        ...form,
        photos,
      });
      navigate(`/service-tickets/${res.data.data.id}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create complaint');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setPhotoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/files', formData);
      const url = res.data.url || res.data.data?.url;
      if (url) setPhotos((p) => [...p, url]);
    } catch {
      const reader = new FileReader();
      reader.onload = () => {
        setPhotos((p) => [...p, reader.result as string]);
      };
      reader.readAsDataURL(file);
    } finally {
      setPhotoUploading(false);
    }
  };

  const removePhoto = (idx: number) => {
    setPhotos((p) => p.filter((_, i) => i !== idx));
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">Complaint Intake</h1>
        <p className="text-sm text-[var(--text-muted)]">Register a new service complaint</p>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6 space-y-6">
        {/* Received From */}
        <div>
          <label className="mb-2 block text-xs font-semibold text-[var(--text-secondary)]">Received From</label>
          <div className="flex gap-2">
            {RECEIVED_FROM.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setForm((f) => ({ ...f, receivedFrom: opt.value }))}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                  form.receivedFrom === opt.value
                    ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)]'
                    : 'border-[var(--border-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-muted)]'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Customer Info */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">
              Customer Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.customerName}
              onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))}
              placeholder="Full name"
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">
              Phone <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={form.customerPhone}
              onChange={(e) => setForm((f) => ({ ...f, customerPhone: e.target.value }))}
              placeholder="+91..."
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">Address</label>
          <textarea
            value={form.customerAddress}
            onChange={(e) => setForm((f) => ({ ...f, customerAddress: e.target.value }))}
            rows={2}
            placeholder="Full address"
            className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>

        {/* Invoice & Product */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">Invoice Number</label>
            <input
              type="text"
              value={form.invoiceNumber}
              onChange={(e) => setForm((f) => ({ ...f, invoiceNumber: e.target.value }))}
              placeholder="INV-0001"
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">Invoice Date</label>
            <input
              type="date"
              value={form.invoiceDate}
              onChange={(e) => setForm((f) => ({ ...f, invoiceDate: e.target.value }))}
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">Serial Number</label>
          <input
            type="text"
            value={form.serialNumber}
            onChange={(e) => setForm((f) => ({ ...f, serialNumber: e.target.value }))}
            placeholder="Product serial number"
            className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>

        {/* Product Type */}
        <div>
          <label className="mb-2 block text-xs font-semibold text-[var(--text-secondary)]">
            Product Type <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-3 gap-3">
            {PRODUCT_TYPES.map((pt) => (
              <button
                key={pt.value}
                type="button"
                onClick={() => setForm((f) => ({ ...f, productType: pt.value }))}
                className={`flex flex-col items-center gap-2 rounded-lg border p-4 text-sm font-medium transition-colors ${
                  form.productType === pt.value
                    ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)]'
                    : 'border-[var(--border-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-muted)]'
                }`}
              >
                <pt.icon size={24} />
                {pt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Problem Description */}
        <div>
          <label className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">
            Problem Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            rows={4}
            placeholder="Describe the issue in detail..."
            className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>

        {/* System Verification */}
        <div>
          <label className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">System Verification Notes</label>
          <textarea
            value={form.systemVerification}
            onChange={(e) => setForm((f) => ({ ...f, systemVerification: e.target.value }))}
            rows={2}
            placeholder="Initial observations or checks performed..."
            className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>

        {/* Photo Upload */}
        <div>
          <label className="mb-2 block text-xs font-medium text-[var(--text-secondary)]">Photos / Videos</label>
          <div className="flex flex-wrap gap-2">
            {photos.map((url, idx) => (
              <div key={idx} className="relative group">
                <img src={url} alt="" className="h-20 w-20 rounded-lg object-cover border border-[var(--border-primary)]" />
                <button
                  type="button"
                  onClick={() => removePhoto(idx)}
                  className="absolute -top-2 -right-2 rounded-full bg-red-500 p-1 text-white hover:bg-red-600"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            <label className="flex h-20 w-20 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-[var(--border-primary)] hover:bg-[var(--bg-muted)]">
              <input type="file" accept="image/*,video/*" onChange={handlePhotoUpload} className="hidden" />
              {photoUploading ? (
                <Loader2 size={20} className="animate-spin text-[var(--text-muted)]" />
              ) : (
                <Camera size={20} className="text-[var(--text-muted)]" />
              )}
            </label>
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-between border-t border-[var(--border-primary)] pt-4">
          <div className="flex items-center gap-2 text-xs">
            {!isComplete ? (
              <>
                <AlertTriangle size={14} className="text-orange-500" />
                <span className="text-orange-600">Incomplete — will be put on hold</span>
              </>
            ) : (
              <>
                <CheckCircle size={14} className="text-green-500" />
                <span className="text-green-600">Complete — ready to submit</span>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/service-tickets')}
              className="rounded-lg border border-[var(--border-primary)] px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-muted)]"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Submit Complaint
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}